import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Eye, EyeOff, Languages } from "lucide-react";

import { login } from "@/api/auth";
import { setTokens } from "@/api/client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTheme } from "@/hooks/useTheme";

/**
 * Login — "Control Room" estetika.
 *
 * Levi panel (lg+): dark monitoring surface — brand mark, claim, mock ESL
 * label kartice koje "sync-uju", live status feed u monospace fontu.
 * Desni panel: čista forma, floating-label inputi sa donjom linijom, show/hide
 * password, sr/en language switcher gore-desno.
 *
 * Sve interaktivne boje idu kroz `--primary` (theme-uje se iz API-ja preko
 * `useTheme`), pa brending boja automatski prelazi na CTA, focus, ring, itd.
 */
export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  // Trigger entrance animacije nakon prvog rendera
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const { access_token, refresh_token } = await login(email, password);
      setTokens(access_token, refresh_token);
      void navigate(isMobile ? "/onboard/quick" : "/", { replace: true });
    } catch (err) {
      const isNetwork = err instanceof Error && /Network|timeout/i.test(err.message);
      setErrorMessage(
        isNetwork ? t("auth.login.error_network") : t("auth.login.error_invalid"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function toggleLang() {
    const next = i18n.language?.startsWith("sr") ? "en" : "sr";
    void i18n.changeLanguage(next);
  }
  const langLabel = i18n.language?.startsWith("sr") ? "EN" : "SR";

  return (
    <div className="relative flex min-h-screen bg-background">
      {/* ============== LEFT — control room panel ============== */}
      <aside
        className="relative hidden shrink-0 overflow-hidden lg:flex lg:w-[480px] xl:w-[44%]"
        aria-hidden="true"
      >
        {/* base */}
        <div className="absolute inset-0 bg-[#0b0f17]" />
        {/* grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse at 30% 20%, black 30%, transparent 75%)",
          }}
        />
        {/* radial glow tied to brand */}
        <div
          className="absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--primary) / 0.55), transparent 60%)",
          }}
        />
        <div className="absolute -bottom-40 -right-24 h-[420px] w-[420px] rounded-full bg-sky-500/10 blur-3xl" />

        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          {/* brand */}
          <div
            className={`flex items-center gap-3 transition-all duration-700 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
            }`}
          >
            {theme.logo_url ? (
              <img
                src={theme.logo_url}
                alt={theme.app_name}
                className="h-9 object-contain"
              />
            ) : (
              <div className="relative flex h-9 w-9 items-center justify-center">
                <span
                  className="absolute inset-0 rounded-md"
                  style={{ background: "hsl(var(--primary))" }}
                />
                <span className="relative font-mono text-sm font-bold text-white">
                  ⌘
                </span>
              </div>
            )}
            <div className="flex flex-col leading-tight">
              <span className="text-base font-semibold tracking-tight text-white">
                {theme.app_name}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                control&nbsp;room
              </span>
            </div>
          </div>

          {/* claim + mock labels */}
          <div className="space-y-10">
            <div
              className={`space-y-5 transition-all duration-700 delay-150 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
            >
              <h2 className="max-w-md text-[2.6rem] font-bold leading-[1.05] tracking-tight text-white">
                Every shelf,{" "}
                <span
                  className="bg-gradient-to-r bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, hsl(var(--primary)), #fb7185)",
                  }}
                >
                  in&nbsp;sync.
                </span>
              </h2>
              <p className="max-w-sm text-sm leading-relaxed text-white/50">
                Real-time product synchronization, template lifecycle and
                multi-store monitoring for modern retail operations.
              </p>
            </div>

            {/* mock labels */}
            <div
              className={`relative h-[180px] w-full max-w-md transition-all duration-700 delay-300 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <MockLabel
                style={{ left: "0", top: "8px", transform: "rotate(-4deg)" }}
                price="€4,29"
                name="Bio mleko 1L"
                sku="SKU 8410-221"
                state="synced"
              />
              <MockLabel
                style={{ left: "150px", top: "0", transform: "rotate(2deg)" }}
                price="€12,90"
                name="Premium kafa 500g"
                sku="SKU 7702-115"
                state="syncing"
                accent
              />
              <MockLabel
                style={{ left: "300px", top: "20px", transform: "rotate(-1deg)" }}
                price="€2,49"
                name="Hleb integralni"
                sku="SKU 1100-007"
                state="synced"
              />
            </div>
          </div>

          {/* status feed */}
          <div
            className={`space-y-3 transition-all duration-700 delay-500 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            <div className="space-y-1.5 font-mono text-[11px]">
              <FeedRow color="emerald">sync engine — running</FeedRow>
              <FeedRow color="emerald">minewesl cloud — connected</FeedRow>
              <FeedRow color="sky">12,847 labels synced today</FeedRow>
            </div>
            {(theme.login_message ?? theme.company_name) && (
              <p className="border-t border-white/5 pt-3 text-[11px] text-white/30">
                {theme.login_message ?? theme.company_name}
              </p>
            )}
          </div>
        </div>
      </aside>

      {/* ============== RIGHT — form panel ============== */}
      <main className="relative flex flex-1 items-center justify-center p-6 sm:p-10">
        {/* lang switcher top-right */}
        <button
          type="button"
          onClick={toggleLang}
          className="absolute right-6 top-6 inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium tracking-wider text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
        >
          <Languages className="h-3.5 w-3.5" aria-hidden="true" />
          {langLabel}
        </button>

        <div
          className={`w-full max-w-sm transition-all duration-700 ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
        >
          {/* mobile brand */}
          <div className="mb-10 flex items-center gap-2.5 lg:hidden">
            <div className="relative flex h-8 w-8 items-center justify-center">
              <span
                className="absolute inset-0 rounded-md"
                style={{ background: "hsl(var(--primary))" }}
              />
              <span className="relative font-mono text-xs font-bold text-white">
                ⌘
              </span>
            </div>
            <span className="text-base font-semibold tracking-tight">
              {theme.app_name}
            </span>
          </div>

          <div className="mb-10">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {theme.app_subtitle ?? t("app.subtitle")}
            </p>
            <h1 className="text-[2rem] font-bold leading-tight tracking-tight">
              {t("auth.login.title")}
              <span style={{ color: "hsl(var(--primary))" }}>.</span>
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <FloatingField
              id="email"
              type="email"
              label={t("auth.login.email")}
              value={email}
              onChange={setEmail}
              autoComplete="username"
              required
              inputMode="email"
            />

            <FloatingField
              id="password"
              type={showPassword ? "text" : "password"}
              label={t("auth.login.password")}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              }
            />

            {errorMessage && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="group relative mt-2 flex w-full items-center justify-center gap-2 overflow-hidden rounded-md bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground shadow-[0_4px_20px_-6px_hsl(var(--primary)/0.5)] transition-all hover:shadow-[0_6px_24px_-4px_hsl(var(--primary)/0.6)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="relative z-10">
                {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
              </span>
              {!submitting && (
                <svg
                  className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8h10m0 0L9 4m4 4l-4 4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <span
                aria-hidden="true"
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full"
              />
            </button>
          </form>

          <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
            v0.1.0 · secured channel
          </p>
        </div>
      </main>
    </div>
  );
}

/* ----------------------------- subcomponents ----------------------------- */

function FloatingField({
  id,
  type,
  label,
  value,
  onChange,
  autoComplete,
  required,
  inputMode,
  trailing,
}: {
  id: string;
  type: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
  inputMode?: "email" | "text";
  trailing?: React.ReactNode;
}) {
  const [focused, setFocused] = useState(false);
  const floated = focused || value.length > 0;
  return (
    <div className="group relative">
      <label
        htmlFor={id}
        className={`pointer-events-none absolute left-0 origin-left transition-all duration-200 ${
          floated
            ? "top-0 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            : "top-7 text-base text-muted-foreground/70"
        }`}
        style={floated && focused ? { color: "hsl(var(--primary))" } : undefined}
      >
        {label}
      </label>
      <div className="relative pt-6">
        <input
          id={id}
          type={type}
          required={required}
          inputMode={inputMode}
          autoComplete={autoComplete}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="peer w-full border-0 border-b border-border bg-transparent px-0 pb-2 pr-8 text-base text-foreground outline-none transition-colors placeholder:text-transparent focus:border-transparent"
        />
        {/* underline indicator */}
        <span
          aria-hidden="true"
          className={`absolute bottom-0 left-0 h-px w-full origin-left transition-transform duration-300 ${
            focused ? "scale-x-100" : "scale-x-0"
          }`}
          style={{ background: "hsl(var(--primary))" }}
        />
        {trailing && (
          <div className="absolute bottom-2 right-0 flex items-center">
            {trailing}
          </div>
        )}
      </div>
    </div>
  );
}

function MockLabel({
  price,
  name,
  sku,
  state,
  accent,
  style,
}: {
  price: string;
  name: string;
  sku: string;
  state: "synced" | "syncing";
  accent?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className="absolute w-[180px] rounded-md border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-3 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] backdrop-blur-md"
      style={style}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="truncate text-[10px] uppercase tracking-[0.12em] text-white/40">
            {sku}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-white/70">{name}</div>
        </div>
        {state === "syncing" ? (
          <span
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full"
            style={{ background: "hsl(var(--primary))" }}
          />
        ) : (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
        )}
      </div>
      <div
        className={`mt-2 font-mono text-2xl font-bold leading-none ${
          accent ? "" : "text-white"
        }`}
        style={accent ? { color: "hsl(var(--primary))" } : undefined}
      >
        {price}
      </div>
      {/* fake barcode */}
      <div className="mt-2.5 flex h-4 items-end gap-[1.5px]">
        {Array.from({ length: 22 }).map((_, i) => (
          <span
            key={i}
            className="bg-white/50"
            style={{
              width: i % 3 === 0 ? 2 : 1,
              height: `${50 + ((i * 37) % 50)}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function FeedRow({
  color,
  children,
}: {
  color: "emerald" | "sky";
  children: React.ReactNode;
}) {
  const dot =
    color === "emerald"
      ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
      : "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.6)]";
  return (
    <div className="flex items-center gap-2 text-white/40">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span>{children}</span>
    </div>
  );
}
